import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Modal, cn } from '../ui';
import { NAV_COMMANDS, filterCommands } from './commandPaletteModel';

/**
 * Global command palette. Opens on ⌘K / Ctrl-K, filters every navigable
 * destination (from {@link NAV_COMMANDS}) with a fuzzy matcher, and navigates on
 * selection. Reuses the accessible {@link Modal} (focus trap + Escape + focus
 * restore); adds ↑/↓ to move the highlight and Enter to activate.
 *
 * Rendered once in the app shell — it manages its own open state and the global
 * key listener, so no wiring is needed at call sites.
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const results = useMemo(() => filterCommands(NAV_COMMANDS, query), [query]);

  // Global ⌘K / Ctrl-K toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Reset query + selection each time the palette opens, and move focus to the
  // search box (this parent effect runs after the child Modal's focus effect,
  // so it wins the initial focus).
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    inputRef.current?.focus();
  }, [open]);

  // Keep the highlight in range and visible as the result set changes.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, results.length - 1)));
  }, [results.length]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = results[activeIndex];
      if (chosen) go(chosen.path);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      ariaLabel="Command palette"
      size="lg"
    >
      <div className="flex items-center gap-2 border-b border-surface-border pb-3">
        <Search className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder="Jump to a page…"
          aria-label="Search pages"
          role="combobox"
          aria-expanded
          aria-controls="command-palette-list"
          className="w-full bg-transparent text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
        />
        <kbd className="hidden rounded border border-surface-border bg-surface-1 px-1.5 py-0.5 text-[10px] text-zinc-500 sm:inline">
          Esc
        </kbd>
      </div>

      {results.length === 0 ? (
        <p className="py-8 text-center text-sm text-zinc-500">
          No pages match “{query}”.
        </p>
      ) : (
        <ul
          ref={listRef}
          id="command-palette-list"
          role="listbox"
          aria-label="Pages"
          className="mt-2 max-h-80 space-y-0.5 overflow-y-auto"
        >
          {results.map((cmd, index) => {
            const Icon = cmd.icon;
            const active = index === activeIndex;
            return (
              <li key={cmd.path} data-index={index} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => go(cmd.path)}
                  onMouseMove={() => setActiveIndex(index)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    active
                      ? 'bg-surface-3 text-zinc-50'
                      : 'text-zinc-300 hover:bg-surface-2',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
                  <span className="flex-1 truncate">{cmd.label}</span>
                  <span className="shrink-0 text-xs text-zinc-500">{cmd.group}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
